/**
 * Collectible Manager
 *
 * Manages all collectibles in the game:
 * - Tracks which items have been collected
 * - Provides statistics (total, collected, per-type breakdown)
 * - Handles reset on timer reset
 * - Emits events for UI updates
 */

import { CollectibleType } from '../entities/Collectible';

export interface CollectibleStats {
  total: number;
  collected: number;
  byType: Record<CollectibleType, { total: number; collected: number }>;
}

export class CollectibleManager {
  private collectedIds: Set<string> = new Set();
  private totalCollectibles: number = 0;
  private collectiblesByType: Map<CollectibleType, Set<string>> = new Map();

  // Event emitter for UI updates
  private scene: Phaser.Scene;

  // Persistence key for collected state
  private static readonly STORAGE_KEY = 'blobo_collected_items';
  private saveThrottleTimer: number = 0;
  private pendingSave: boolean = false;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.initializeTypeMap();
    this.loadCollectedState(); // Restore collected items from previous session
  }

  /**
   * Load collected state from local storage
   * This persists collected items across sessions (app restarts, browser closes, etc.)
   */
  private loadCollectedState(): void {
    try {
      const saved = localStorage.getItem(CollectibleManager.STORAGE_KEY);
      if (saved) {
        const collectedArray = JSON.parse(saved) as string[];
        this.collectedIds = new Set(collectedArray);
        console.log(`📦 Restored ${this.collectedIds.size} collected items from localStorage`);
      }
    } catch (e) {
      console.warn('Failed to load collected state:', e);
    }
  }

  /**
   * Save collected state to local storage (throttled to max once per second)
   */
  private saveCollectedState(): void {
    const now = Date.now();

    // If we saved less than 1000ms ago, just mark as pending
    if (now - this.saveThrottleTimer < 1000) {
      this.pendingSave = true;
      return;
    }

    // Actually save
    try {
      const collectedArray = Array.from(this.collectedIds);
      localStorage.setItem(CollectibleManager.STORAGE_KEY, JSON.stringify(collectedArray));
      this.saveThrottleTimer = now;
      this.pendingSave = false;
    } catch (e) {
      console.warn('Failed to save collected state:', e);
    }
  }

  /**
   * Force save if there's a pending save (called on cleanup/pause)
   */
  public flushPendingSave(): void {
    if (this.pendingSave) {
      try {
        const collectedArray = Array.from(this.collectedIds);
        localStorage.setItem(CollectibleManager.STORAGE_KEY, JSON.stringify(collectedArray));
        this.pendingSave = false;
      } catch (e) {
        console.warn('Failed to flush collected state:', e);
      }
    }
  }

  /**
   * Initialize the type map with all collectible types
   */
  private initializeTypeMap(): void {
    for (const type of Object.values(CollectibleType)) {
      this.collectiblesByType.set(type as CollectibleType, new Set());
    }
  }

  /**
   * Register a collectible (called when spawning)
   */
  public register(id: string, type: CollectibleType): void {
    const typeSet = this.collectiblesByType.get(type);
    if (typeSet) {
      // Only increment total if this is a new ID (not already registered)
      const wasNew = !typeSet.has(id);
      typeSet.add(id);
      if (wasNew) {
        this.totalCollectibles++;
      }
    }
  }

  /**
   * Mark a collectible as collected
   */
  public collect(id: string, type: CollectibleType): boolean {
    // Check if already collected
    if (this.collectedIds.has(id)) {
      return false;
    }

    // Mark as collected
    this.collectedIds.add(id);

    // Persist to session storage
    this.saveCollectedState();

    // Emit event for UI update
    this.scene.events.emit('collectible:collected', {
      id,
      type,
      total: this.totalCollectibles,
      collected: this.collectedIds.size,
    });

    return true;
  }

  /**
   * Check if a collectible has been collected
   */
  public isCollected(id: string): boolean {
    return this.collectedIds.has(id);
  }

  /**
   * Get total number of collectibles
   */
  public getTotal(): number {
    return this.totalCollectibles;
  }

  /**
   * Get number of collected items
   */
  public getCollected(): number {
    return this.collectedIds.size;
  }

  /**
   * Get detailed statistics
   */
  public getStats(): CollectibleStats {
    const byType: Record<CollectibleType, { total: number; collected: number }> = {} as any;

    for (const [type, ids] of this.collectiblesByType.entries()) {
      const total = ids.size;
      const collected = Array.from(ids).filter(id => this.collectedIds.has(id)).length;
      byType[type] = { total, collected };
    }

    return {
      total: this.totalCollectibles,
      collected: this.collectedIds.size,
      byType,
    };
  }

  /**
   * Get collectibles by type
   */
  public getTypeStats(type: CollectibleType): { total: number; collected: number } {
    const typeSet = this.collectiblesByType.get(type);
    if (!typeSet) {
      return { total: 0, collected: 0 };
    }

    const total = typeSet.size;
    const collected = Array.from(typeSet).filter(id => this.collectedIds.has(id)).length;
    return { total, collected };
  }

  /**
   * Reset collection state (called when timer resets)
   */
  public reset(): void {
    this.collectedIds.clear();

    // Clear persisted state
    try {
      localStorage.removeItem(CollectibleManager.STORAGE_KEY);
    } catch (e) {
      console.warn('Failed to clear collected state:', e);
    }

    // Emit reset event
    this.scene.events.emit('collectible:reset', {
      total: this.totalCollectibles,
    });
  }

  /**
   * Get completion percentage
   */
  public getCompletionPercentage(): number {
    if (this.totalCollectibles === 0) return 0;
    return Math.round((this.collectedIds.size / this.totalCollectibles) * 100);
  }

  /**
   * Check if all collectibles are collected
   */
  public isComplete(): boolean {
    return this.collectedIds.size === this.totalCollectibles && this.totalCollectibles > 0;
  }

  /**
   * Get user-friendly type name
   */
  public static getTypeName(type: CollectibleType): string {
    switch (type) {
      case CollectibleType.FIREFLY:
        return 'Fireflies';
      case CollectibleType.SNAIL:
        return 'Snails';
      case CollectibleType.FROG:
        return 'Frogs';
      case CollectibleType.BUTTERFLY:
        return 'Butterflies';
      case CollectibleType.MOTH:
        return 'Moths';
      case CollectibleType.GEM:
        return 'Gems';
      case CollectibleType.COIN:
        return 'Coins';
      case CollectibleType.STAR:
        return 'Stars';
      case CollectibleType.BEETLE:
        return 'Beetles';
      case CollectibleType.BIRD:
        return 'Birds';
      case CollectibleType.DEWDROP:
        return 'Dewdrops';
      case CollectibleType.MUSHROOM:
        return 'Mushrooms';
      default:
        return 'Unknown';
    }
  }

  /**
   * Get emoji icon for type (for UI display)
   */
  public static getTypeIcon(type: CollectibleType): string {
    switch (type) {
      case CollectibleType.FIREFLY:
        return '✨';
      case CollectibleType.SNAIL:
        return '🐌';
      case CollectibleType.FROG:
        return '🐸';
      case CollectibleType.BUTTERFLY:
        return '🦋';
      case CollectibleType.MOTH:
        return '🦋';
      case CollectibleType.GEM:
        return '💎';
      case CollectibleType.COIN:
        return '🪙';
      case CollectibleType.STAR:
        return '⭐';
      case CollectibleType.BEETLE:
        return '🪲';
      case CollectibleType.BIRD:
        return '🐦';
      case CollectibleType.DEWDROP:
        return '💧';
      case CollectibleType.MUSHROOM:
        return '🍄';
      default:
        return '❓';
    }
  }
}
